// src/utils/gameLogic.js
import { zones } from '../data/zones';
import { monsters } from '../data/monsters';
import { npcs, quests } from '../data/npcInteractions';
import { loot } from '../data/loot';

let lootCache = null;
let spawnedNpcs = {}; // Track spawned NPCs per zone to maintain consistency

const WORKER_URL = 'https://cryptwalkergame-worker.patrick-vida.workers.dev';

// Helper function to sanitize input strings
const sanitizeInput = (input) => {
  if (typeof input !== 'string') return input;
  return input.replace(/[<>&"']/g, (match) => ({
    '<': '&lt;',
    '>': '&gt;',
    '&': '&amp;',
    '"': '&quot;',
    "'": '&#x27;',
  }[match]));
};

// Authenticated fetch helper (still needed for leaderboard updates)
const authFetch = async (url, options = {}) => {
  const token = localStorage.getItem('token');
  const headers = {
    'Content-Type': 'application/json',
    ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
    ...options.headers,
  };

  const response = await fetch(url, { ...options, headers });
  if (response.status === 401) {
    localStorage.removeItem('token');
    localStorage.removeItem('userId');
    localStorage.removeItem('characterId');
    localStorage.removeItem('isLoggedIn');
    localStorage.removeItem('currentUsername');
    throw new Error('Unauthorized - Token expired or invalid');
  }
  return response;
};

// New function to get static loot
export function getLootStatic() {
  if (lootCache) return lootCache;

  lootCache = loot.map(item => ({
    ...item,
    name: sanitizeInput(item.name),
    type: sanitizeInput(item.type),
    atk: Number(item.atk || 0),
    def: Number(item.def || 0),
    hp: Number(item.hp || 0),
    damage: Number(item.damage || 0),
    crit_chance: Number(item.crit_chance || 0),
    crit_damage: Number(item.crit_damage || 0),
    mana: Number(item.mana || 0),
    max_mana: Number(item.max_mana || 0),
    dodge_chance: Number(item.dodge_chance || 0),
    status_resist: Number(item.status_resist || 0),
    luck: Number(item.luck || 0),
    drop_chance: Number(item.drop_chance || 0.3),
    ectoplasm_cost: Number(item.ectoplasm_cost || 0),
    rarity: sanitizeInput(item.rarity),
    lore: sanitizeInput(item.lore),
    zone_id: Number(item.zone_id || 1),
    monster_id: Number(item.monster_id || 1),
    level_requirement: Number(item.levelRequirement || 1),
    range: Number(item.range || 0),
    equip_slot: sanitizeInput(item.equip_slot || 'none'),
  }));

  return lootCache;
}

function getNpcsByZone(zoneId) {
  return npcs.filter(npc => npc.zoneId === zoneId).map(npc => ({
    ...npc,
    name: sanitizeInput(npc.name),
    dialogue: Array.isArray(npc.dialogue) ? npc.dialogue : JSON.parse(npc.dialogue || '[]'),
    position_x: Number(npc.position_x || 0),
    position_y: Number(npc.position_y || 0),
    quest_id: Number(npc.available_quests?.[0] || 0),
  }));
}

function getQuestsByZone(zoneId) {
  return quests.filter(quest => quest.zoneId === zoneId).map(quest => ({
    ...quest,
    title: sanitizeInput(quest.title),
    description: sanitizeInput(quest.description),
    objective_type: sanitizeInput(quest.objective_type),
    objective_target: sanitizeInput(quest.objective_target),
    objective_count: Number(quest.objective_count),
    reward_type: sanitizeInput(quest.reward_type),
    reward_value: quest.reward_type === 'item' ? sanitizeInput(quest.reward_value) : Number(quest.reward_value),
    reward_amount: Number(quest.reward_amount),
  }));
}

export function calculateXP(enemy, playerLevel) {
  // Input validation
  if (!enemy || typeof playerLevel !== 'number' || playerLevel <= 0) {
    console.error('Invalid inputs to calculateXP:', { enemy, playerLevel });
    return 10; // Increase fallback to a more reasonable value
  }

  // Extract enemy properties with fallbacks
  const { hp, atk, def, level_min, level_max, rarity, isBoss } = enemy;
  const safeHp = Number(hp) || 10; // Default to 10 if hp is missing
  const safeAtk = Number(atk) || 1; // Default to 1 if atk is missing
  const safeDef = Number(def) || 1; // Default to 1 if def is missing
  const safeLevelMin = Number(level_min) || 1;
  const safeLevelMax = Number(level_max) || 1;
  const averageLevel = (safeLevelMin + safeLevelMax) / 2;

  // Step 1: Calculate base XP based on monster stats
  const baseXP = safeHp * 0.8 + safeAtk * 2.5 + safeDef * 2.0; // Adjusted weights for better scaling

  // Step 2: Adjust XP based on level difference
  const levelDifference = averageLevel - playerLevel;
  let levelMultiplier;
  if (levelDifference >= 0) {
    // Enemy is at or above player's level: reward more XP
    levelMultiplier = 1 + levelDifference * 0.1; // 10% bonus per level above player
  } else {
    // Enemy is below player's level: reduce XP
    levelMultiplier = Math.max(0.3, 1 + levelDifference * 0.2); // Reduce XP, but not below 30%
  }

  // Step 3: Apply rarity multiplier
  const rarityMultipliers = {
    Common: 1.0,
    Uncommon: 1.2,
    Rare: 1.5,
    Elite: 1.8,
    Legendary: 2.0,
  };
  const rarityMultiplier = rarityMultipliers[sanitizeInput(rarity)] || 1.0;

  // Step 4: Apply boss bonus
  const bossMultiplier = isBoss ? 1.5 : 1.0; // 50% bonus for bosses

  // Step 5: Calculate final XP with scaling
  const scalingFactor = 1.0; // Remove the aggressive 0.5 scaling
  let finalXP = baseXP * levelMultiplier * rarityMultiplier * bossMultiplier * scalingFactor;

  // Step 6: Add slight variation for randomness
  const variation = 0.9 + Math.random() * 0.2; // 90% to 110% variation
  finalXP = Math.max(5, Math.round(finalXP * variation)); // Ensure minimum XP of 5

  return finalXP;
}

async function getNextZone(currentZoneId, playerLevel) {
  const zonesList = zones;
  const currentZone = zonesList.find(z => z.id === currentZoneId);

  if (!currentZone) {
    return zonesList.find(z => z.region === 'Shattered Vestibule' && z.level_range_min <= playerLevel && z.level_range_max >= playerLevel) || zonesList[0];
  }

  const regions = {};
  zonesList.forEach(zone => {
    if (!regions[zone.region]) regions[zone.region] = [];
    regions[zone.region].push(zone);
  });

  Object.keys(regions).forEach(region => {
    regions[region].sort((a, b) => a.level_range_min - b.level_range_min);
  });

  const currentRegionZones = regions[currentZone.region];
  const currentIndex = currentRegionZones.findIndex(z => z.id === currentZone.id);

  if (currentIndex === currentRegionZones.length - 1) {
    const allRegions = ['Shattered Vestibule', 'Flooded Catacombs', 'Cursed Expanse Wastes', 'Obsidian Forges', 'Veilspires', 'Throne of Eternity', 'Hollow Depths', 'Shattered Veil', 'Cursed Highlands', 'Dead Marshes'];
    const currentRegionIndex = allRegions.indexOf(currentZone.region);
    if (currentRegionIndex < allRegions.length - 1) {
      const nextRegion = allRegions[currentRegionIndex + 1];
      const nextRegionZones = regions[nextRegion];
      const nextZone = nextRegionZones.find(z => z.level_range_min <= playerLevel && z.level_range_max >= playerLevel);
      return nextZone || currentZone;
    }
    return currentZone;
  }

  const nextZone = currentRegionZones[currentIndex + 1];
  return nextZone && nextZone.level_range_min <= playerLevel && nextZone.level_range_max >= playerLevel ? nextZone : currentZone;
}

export async function generateZone(level, currentZoneId = null) {
  const zonesList = zones;
  if (currentZoneId) {
    const nextZone = await getNextZone(currentZoneId, level);
    return {
      ...nextZone,
      name: sanitizeInput(nextZone.name),
      region: sanitizeInput(nextZone.region),
      description: sanitizeInput(nextZone.description),
      effect: sanitizeInput(nextZone.effect),
      effect_description: sanitizeInput(nextZone.effect_description),
      enemy_types: Array.isArray(nextZone.enemy_types) ? nextZone.enemy_types.map(sanitizeInput) : [],
    };
  } else {
    const eligibleZones = zonesList.filter(zone => level >= zone.level_range_min && level <= zone.level_range_max);
    if (eligibleZones.length === 0) return { name: `Unknown Zone - Level ${level}`, effect: 'none', effect_description: 'Neutral conditions', description: 'No special effect' };
    const zone = eligibleZones[Math.floor(Math.random() * eligibleZones.length)];
    return {
      ...zone,
      name: sanitizeInput(zone.name),
      region: sanitizeInput(zone.region),
      description: sanitizeInput(zone.description),
      effect: sanitizeInput(zone.effect),
      effect_description: sanitizeInput(zone.effect_description),
      enemy_types: Array.isArray(zone.enemy_types) ? zone.enemy_types.map(sanitizeInput) : [],
    };
  }
}

export async function generateFloor(level, currentZoneId = null, isBoss = false) {
  const size = isBoss ? 15 : 10; // 15x15 for boss maps, 10x10 for regular maps
  const center = Math.floor(size / 2); // Center position for player starting point
  const floor = Array(size).fill().map(() => Array(size).fill('floor'));

  // Place more walls for larger maps (15 for regular, 30 for boss maps)
  const numWalls = isBoss ? 30 : 15;
  for (let i = 0; i < numWalls; i++) {
    let x, y;
    do {
      x = Math.floor(Math.random() * size);
      y = Math.floor(Math.random() * size);
    } while (x === center && y === center); // Avoid placing walls at the player's starting position
    floor[y][x] = 'wall';
  }

  // Place loot (more loot on boss maps)
  const numLoot = isBoss ? 3 : 1;
  for (let i = 0; i < numLoot; i++) {
    if (Math.random() < 0.5) { // 50% chance per loot spawn
      let lootX, lootY;
      do {
        lootX = Math.floor(Math.random() * size);
        lootY = Math.floor(Math.random() * size);
      } while (floor[lootY][lootX] !== 'floor' || (lootX === center && lootY === center));
      floor[lootY][lootX] = 'loot';
    }
  }

  // Place exit
  let exitX, exitY;
  do {
    exitX = Math.floor(Math.random() * size);
    exitY = Math.floor(Math.random() * size);
  } while (floor[exitY][exitX] !== 'floor' || (exitX === center && exitY === center));
  floor[exitY][exitX] = 'exit';

  // Place portal (less likely on boss maps)
  let portalX, portalY;
  if (Math.random() < (isBoss ? 0.05 : 0.1)) {
    do {
      portalX = Math.floor(Math.random() * size);
      portalY = Math.floor(Math.random() * size);
    } while (floor[portalY][portalX] !== 'floor' || (portalX === center && portalY === center) || (portalX === exitX && portalY === exitY));
    floor[portalY][portalX] = 'portal';
  }

  // Ensure the map is fully connected using a flood-fill algorithm
  const visited = Array(size).fill().map(() => Array(size).fill(false));
  const queue = [[center, center]];
  visited[center][center] = true;

  while (queue.length > 0) {
    const [x, y] = queue.shift();
    const directions = [[0, 1], [1, 0], [0, -1], [-1, 0]];
    for (const [dx, dy] of directions) {
      const nx = x + dx;
      const ny = y + dy;
      if (nx >= 0 && nx < size && ny >= 0 && ny < size && !visited[ny][nx] && floor[ny][nx] !== 'wall') {
        visited[ny][nx] = true;
        queue.push([nx, ny]);
      }
    }
  }

  // Carve a path to the exit if it's not reachable
  if (!visited[exitY][exitX]) {
    let x = center, y = center;
    while (x !== exitX || y !== exitY) {
      if (Math.abs(exitX - x) > Math.abs(exitY - y)) {
        x += exitX > x ? 1 : -1;
      } else {
        y += exitY > y ? 1 : -1;
      }
      if (floor[y][x] === 'wall') floor[y][x] = 'floor';
    }
  }

  // Place NPC
  let npc = null;
  const zone = await generateZone(level, currentZoneId);
  const zoneId = zone.id;

  if (currentZoneId && currentZoneId !== zoneId) delete spawnedNpcs[currentZoneId];

  const zoneNpcs = getNpcsByZone(zoneId);
  if (zoneNpcs.length > 0) {
    let selectedNpc = spawnedNpcs[zoneId] ? zoneNpcs.find(n => n.id === spawnedNpcs[zoneId].id) : null;
    if (!selectedNpc) {
      const questNpcs = zoneNpcs.filter(npc => npc.available_quests?.length > 0);
      selectedNpc = questNpcs.length > 0 ? questNpcs[Math.floor(Math.random() * questNpcs.length)] : zoneNpcs[Math.floor(Math.random() * zoneNpcs.length)];
      spawnedNpcs[zoneId] = { ...selectedNpc };
    }

    let npcX = selectedNpc.position_x || Math.floor(Math.random() * size);
    let npcY = selectedNpc.position_y || Math.floor(Math.random() * size);

    npcX = Math.max(0, Math.min(size - 1, npcX));
    npcY = Math.max(0, Math.min(size - 1, npcY));

    const invalidPositions = [[center, center], [exitX, exitY], [portalX, portalY]].filter(pos => pos[0] !== undefined);
    let attempts = 0;
    const maxAttempts = 50; // Increased attempts for larger maps

    let isInvalidPosition = true;
    while (isInvalidPosition && attempts < maxAttempts) {
      isInvalidPosition = floor[npcY][npcX] === 'wall';
      if (!isInvalidPosition) {
        for (const [x, y] of invalidPositions) {
          if (x === npcX && y === npcY) {
            isInvalidPosition = true;
            break;
          }
        }
      }
      if (isInvalidPosition) {
        npcX = Math.floor(Math.random() * size);
        npcY = Math.floor(Math.random() * size);
        attempts++;
      }
    }

    if (attempts >= maxAttempts) {
      npc = null;
    } else {
      npc = { x: npcX, y: npcY, data: { ...selectedNpc, name: sanitizeInput(selectedNpc.name) } };
    }
  }

  return { floor, npc, zoneId: zone.id, isBoss };
}

export async function spawnEnemy(level, zoneEffect, zoneId = null) {
  const zone = zoneId ? zones.find(z => z.id === zoneId) : await generateZone(level);
  const enemyTypes = Array.isArray(zone.enemy_types)
    ? zone.enemy_types.map(sanitizeInput)
    : JSON.parse(JSON.stringify(zone.enemy_types || '[]')).map(sanitizeInput);

  const eligibleMonsters = monsters.filter(
    (monster) =>
      enemyTypes.includes(monster.name) &&
      level >= monster.level_min &&
      level <= monster.level_max &&
      monster.zone_id === zone.id
  );

  if (!eligibleMonsters.length) {
    const defaultEnemy = {
      type: 'Unknown Creature',
      hp: Math.max(1, 10 + level * 0.5),
      maxHp: Math.max(1, 10 + level * 0.5),
      atk: Math.max(1, 1 + level * 0.2),
      def: Math.max(1, 1 + level * 0.1),
      x: Math.floor(Math.random() * 5),
      y: Math.floor(Math.random() * 5),
      abilities: [],
      level_min: 1,
      level_max: 1,
      rarity: 'Common',
      isBoss: false,
    };
    return [defaultEnemy]; // Return as an array
  }

  // Determine the number of enemies to spawn (1 to 3)
  const spawnRoll = Math.random();
  let numEnemies;
  if (spawnRoll < 0.5) numEnemies = 1; // 50% chance for 1 enemy
  else if (spawnRoll < 0.8) numEnemies = 2; // 30% chance for 2 enemies
  else numEnemies = 3; // 20% chance for 3 enemies

  // Decide if enemies are the same type or a mixture
  const sameType = Math.random() < 0.5; // 50% chance for same type, 50% for mixed types

  const enemies = [];
  const size = eligibleMonsters.some(m => m.is_boss) ? 15 : 10; // Use larger map size for bosses

  for (let i = 0; i < numEnemies; i++) {
    let selectedMonster;
    if (sameType && i > 0) {
      // Use the same monster type as the first one
      selectedMonster = enemies[0].monsterData;
    } else {
      // Pick a random monster from the eligible list
      selectedMonster = eligibleMonsters[Math.floor(Math.random() * eligibleMonsters.length)];
    }

    let hp = Math.max(1, selectedMonster.base_hp + level * 0.5);
    let atk = Math.max(1, selectedMonster.base_atk + level * 0.2);
    let def = Math.max(1, selectedMonster.base_def + level * 0.1);

    // Apply zone effects
    switch (zoneEffect) {
      case 'blood':
        hp += 5;
        break;
      case 'fire':
        atk += 2;
        break;
      case 'earth':
        def += 2;
        break;
      case 'illusion':
        def += 1;
        atk -= 1;
        break;
      case 'darkness':
        atk += 1;
        break;
      case 'storm':
        hp += 2;
        atk += 1;
        break;
      case 'poison':
        hp += 3;
        break;
      default:
        break;
    }

    const abilities = selectedMonster.abilities
      ? JSON.parse(JSON.stringify(selectedMonster.abilities))
      : [];

    // Ensure unique positions for each enemy
    let x, y;
    let attempts = 0;
    const maxAttempts = 50;
    do {
      x = Math.floor(Math.random() * size);
      y = Math.floor(Math.random() * size);
      attempts++;
    } while (
      enemies.some(e => e.x === x && e.y === y) && // Avoid overlapping positions
      attempts < maxAttempts
    );

    if (attempts >= maxAttempts) {
      // If we can't find a unique position, skip this enemy
      continue;
    }

    enemies.push({
      type: sanitizeInput(selectedMonster.name),
      hp,
      maxHp: hp,
      atk,
      def,
      x,
      y,
      abilities,
      level: Math.min(level, selectedMonster.level_max),
      level_min: selectedMonster.level_min,
      level_max: selectedMonster.level_max,
      rarity: sanitizeInput(selectedMonster.rarity),
      isBoss: selectedMonster.is_boss || false,
      monsterData: selectedMonster, // Store the monster data for same-type spawning
    });
  }

  return enemies;
}

export async function combat(player, enemy, zone) {
  let modifiedAtk = Math.max(0, player.atk || 0);
  let playerDmg, enemyDmg;

  if (player.tempBoosts?.atk5Floors?.turnsLeft > 0) {
    modifiedAtk += player.tempBoosts.atk5Floors.value;
  }
  if (player.tempBoosts?.critChance5Floors?.turnsLeft > 0) {
    const totalCritChance = Math.min(1, (player.crit_chance || 0.05) + player.tempBoosts.critChance5Floors.value + (player.luck * 0.01));
    const isCrit = Math.random() < totalCritChance;
    if (isCrit) {
      modifiedAtk *= Math.max(1, (player.crit_damage || 1.5) + (player.tempBoosts.critChance5Floors.value * 10));
    }
  }
  if (player.tempBoosts?.dodgeChance5Floors?.turnsLeft > 0) {
    player.dodge_chance = Math.min(1, (player.dodge_chance || 0.05) + player.tempBoosts.dodgeChance5Floors.value);
  }
  if (player.tempBoosts?.def5Floors?.turnsLeft > 0) {
    player.def = Math.max(0, (player.def || 1) + player.tempBoosts.def5Floors.value);
  }

  if (zone?.effect === 'frost') modifiedAtk = Math.floor(modifiedAtk * 0.8);
  else if (zone?.effect === 'veil') modifiedAtk = Math.floor(modifiedAtk * 0.9);

  playerDmg = Math.max(0, modifiedAtk - (enemy.def || 0));
  enemyDmg = Math.max(0, (enemy.atk || 0) - (player.def || 0));

  if (Math.random() < (player.dodge_chance || 0)) {
    enemyDmg = 0;
  }

  const newPlayer = {
    ...player,
    hp: Math.max(0, (player.hp || 0) - enemyDmg),
  };
  const newEnemy = {
    ...enemy,
    hp: Math.max(0, (enemy.hp || 0) - playerDmg),
    maxHp: enemy.maxHp || enemy.hp,
  };

  return { player: newPlayer, enemy: newEnemy };
}

export async function applyLoot(player) {
  const lootItems = getLootStatic();
  const luckModifier = Math.max(0, 1 + (player.luck || 0) * 0.1);
  const gold = Math.floor((Math.random() * 5 + 1) * luckModifier);
  let lootItem = null;
  let ectoplasmGain = 0;

  const currentZoneId = player.zone?.id || 1;
  const playerLevel = player.level || 1;

  const eligibleItems = lootItems.filter(item =>
    item.zone_id === currentZoneId &&
    item.level_requirement <= playerLevel
  );

  const itemRoll = Math.random() * luckModifier;
  const dropCandidates = eligibleItems.filter(item => itemRoll < item.drop_chance * luckModifier);

  if (dropCandidates.length > 0) {
    lootItem = dropCandidates[Math.floor(Math.random() * dropCandidates.length)];
  }

  if (!lootItem && Math.random() < 0.05 * luckModifier) {
    const commonItems = eligibleItems.filter(item => item.rarity.toLowerCase() === 'common');
    if (commonItems.length > 0) {
      lootItem = commonItems[Math.floor(Math.random() * commonItems.length)];
    }
  }

  if (Math.random() < 0.05 * luckModifier) {
    ectoplasmGain = Math.floor(Math.random() * 3 + 1);
  }

  let newEctoplasm = Math.max(0, (player.ectoplasm || 0) + ectoplasmGain);
  if (player.zone && player.zone.effect === 'ethereal') newEctoplasm += 2;

  const updatedPlayer = {
    ...player,
    gold: Math.max(0, (player.gold || 0) + gold),
    ectoplasm: newEctoplasm,
  };

  if (lootItem && updatedPlayer.inventory && updatedPlayer.inventory.length < 5) {
    updatedPlayer.inventory.push({ ...lootItem, name: sanitizeInput(lootItem.name) });
  }

  return {
    player: updatedPlayer,
    gold,
    lootItem,
    ectoplasm: ectoplasmGain,
  };
}